import React, { useRef, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Form } from 'react-final-form';
import Button from '@saucelabs/ds-button/lib';
import { Card, CardContent } from '@saucelabs/ds-card';
import { InlineAlert } from '@saucelabs/ds-alert';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@saucelabs/ds-typography/lib';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { IconLockFill, IconQuestionOutline } from '@saucelabs/ds-icons';
import validate from '@saucelabs/sl-util-validate';
import { DSInputText, DSSelect } from '@saucelabs/sl-component-form';
import ValidationError from 'ajv/dist/runtime/validation_error';

import { getCountries, getStateByCountryCode } from '../../utils/locations';
import { COUNTRIES_ZIP_MANDATORY, ROUTE_PARENT_SECTION, ROUTE_SECTION_CHECKOUT } from '../../constants';
import { formatPaymentFormValues, getPostalCodeValidationRegexFor } from '../../utils';
import {
  fetchRecurlyTokenAction,
  paymentInfoSavedAction,
  retrieveAccountAction,
  setRecurlyInstance,
  updateBillingInfoAction,
} from '../../ducks/billing-info/actions';
import { ensureRecurlyInstance } from '../../ducks/billing-info/operations';
import { paymentFormSelector } from '../../ducks/billing-info/selectors';
import { CreditCardDetails } from './CreditCardDetails';
import Styles from './CreditCard.styles';
import RecurlyHelper from '../RecurlyHelper';
import trackEvent, { EVENTS } from '../../tracking';
import { handle3DSecure } from '../../utils/threeDSecure';

const CountryInfoIcon = () => (
  <OverlayTrigger
    delay={{ hide: 300 }}
    overlay={(
      <Tooltip id="eea-tooltip">
        This selection determines where your billing data is stored
        - if you select a country within the European Economic Area (EEA),
        your data will be stored within the EU. Once selected, you may only
        change your country within the same region e.g. if you select a country in
        EEA, you may only change to another country within the EEA.
        The same applies to countries outside of the EEA.
      </Tooltip>
    )}
    placement="top"
  >
    <IconQuestionOutline className="w-2 h-2 fill-current text-gray-500" />
  </OverlayTrigger>
);

interface SelectLabelProps {
  label: string;
}

const SelectLabel: React.FC<SelectLabelProps> = ({ label }) => (
  <Typography
    className="font-semibold text-gray-900"
    disableSpacing
    variant="body-large"
  >
    {label}
  </Typography>
);

const constraints = {
  company: {
    length: { maximum: 100 },
    presence: { allowEmpty: false, message: 'Please enter your company name' },
  },
  email: {
    length: { maximum: 255 },
    presence: { allowEmpty: false, message: 'Please enter your email' },
    email: true,
  },
  address1: {
    length: { maximum: 50 },
    presence: { allowEmpty: false, message: 'Please enter your street address' },
  },
  country: {
    presence: { allowEmpty: false, message: 'Please enter your country' },
  },
  state: {
    presence: { allowEmpty: false, message: 'Please enter your state' },
  },
  city: {
    length: { maximum: 50 },
    presence: { allowEmpty: false, message: 'Please enter your city' },
  },
  postalCode: (value, attributes) => {
    const baseConstraints = {
      length: { maximum: 10 },
    };
    // The postal code is mandatory only for certain countries
    if (COUNTRIES_ZIP_MANDATORY.includes(attributes.country)) {
      return {
        ...baseConstraints,
        presence: { allowEmpty: false, message: 'Please enter your zip code' },
        format: (val, attrs) => {
          const { country } = attrs;
          const postalCodeRegex = getPostalCodeValidationRegexFor(country);
          return postalCodeRegex?.validationRegex;
        },
      };
    }
    return baseConstraints;
  },
  firstName: {
    length: { maximum: 50 },
    presence: { allowEmpty: false, message: 'Please enter cardholder name' },
  },
  lastName: {
    length: { maximum: 50 },
    presence: { allowEmpty: false, message: 'Please enter cardholder name' },
  },
  vatNumber: {
    length: { maximum: 20 },
  },
};

const FormErrorsInlineAlert = () => (
  <InlineAlert
    className="mb-2"
    color="danger"
    data-qa="contact-details-errors"
  >
    <Typography
      className="text-high-emphasis font-semibold"
      disableSpacing
      variant="body-large"
    >
      We could not continue due to errors in the form
    </Typography>
    <div className="flex flex-row">
      <Typography
        className="text-high-emphasis"
        disableSpacing
        variant="body-large"
      >
        Please fix the fields marked in red and try again.
      </Typography>
    </div>
  </InlineAlert>
);

const PaymentAuthenticationInlineAlert = () => (
  <InlineAlert
    className="mb-2"
    color="info"
    data-qa="credit-card-info"
  >
    <Typography
      className="text-high-emphasis font-semibold"
      disableSpacing
      variant="body-large"
    >
      Payment authentication.
    </Typography>
    <Typography
      className="text-high-emphasis"
      disableSpacing
      variant="body-large"
    >
      For extra security, your card issuer may require additional verification.
    </Typography>
  </InlineAlert>
);

const CreditCardNotAcceptedInlineAlert = () => (
  <InlineAlert
    className="mb-2"
    color="danger"
    data-qa="credit-card-errors"
  >
    <Typography
      className="text-high-emphasis font-semibold"
      disableSpacing
      variant="body-large"
    >
      The credit card was not accepted
    </Typography>
    <div className="flex flex-row">
      <Typography
        className="text-high-emphasis mr-0.5"
        disableSpacing
        variant="body-large"
      >
        The transaction was not approved by your bank. Please try another payment method, or
      </Typography>
      <Typography
        className="text-high-emphasis"
        disableSpacing
        variant="body-large"
      >
        <a
          className={Styles.link}
          href="https://support.saucelabs.com/hc/en-us"
          rel="noreferrer"
          target="_blank"
        >
          contact support
        </a>
        <span>.</span>
      </Typography>
    </div>

  </InlineAlert>
);

const CreditCardNotShownUntilCountryKnown = () => (
  <InlineAlert
    className="mb-2"
    color="info"
  >
    <div className="flex flex-row">
      <Typography
        className="text-high-emphasis mr-0.5"
        disableSpacing
        variant="body-large"
      >
        You can enter credit card details after completing Company &amp; Contact Details.
      </Typography>
    </div>

  </InlineAlert>
);

const ContinueButton = ({ onContinueClicked, setSubmitted }) => {
  const [verifying, setVerifying] = useState(false);

  const handleButtonSubmit = async () => {
    setVerifying(true);

    await onContinueClicked();

    setSubmitted(true);
    setVerifying(false);
  };

  return (
    <Button
      className="mr-3"
      disabled={verifying}
      onPress={handleButtonSubmit}
      type="submit"
    >
      {verifying ? 'Verifying...' : 'Continue'}
    </Button>
  );
};

export const CheckoutDetailsForm = () => {
  const billingInfo = useSelector(paymentFormSelector);
  const { initialValues, recurlyInstance } = billingInfo;

  const dispatch = useDispatch();

  const formRef = useRef();
  const countrySelectRef = useRef();
  const stateSelectRef = useRef();

  const [countryOptions] = useState(getCountries());
  const [submitted, setSubmitted] = useState(false);

  const [recurlyJsCardObject, setRecurlyJsCardObject] = useState();
  const [readOnlyCard, setReadOnlyCard] = useState();
  const [cardVerified, setCardVerified] = useState();

  const recurlyOnChange = (recurlyJSCard) => {
    setRecurlyJsCardObject(recurlyJSCard.fields.card);
  };

  const afterRecurlyLoad = React.useCallback(() => {
    recurly.on('change', recurlyOnChange, []);
  });

  const handleReplaceCard = () => {
    setReadOnlyCard(false);
    trackEvent(EVENTS.CHECKOUT_REPLACE_CARD);
  };

  const history = useHistory();

  useEffect(() => {
    dispatch(ensureRecurlyInstance());
  }, [dispatch]);

  useEffect(() => {
    dispatch(retrieveAccountAction())
      .then((response) => {
        if (response?.data === null) {
          setReadOnlyCard(false);
        } else {
          setReadOnlyCard(response?.data?.billing_info?.last_four !== undefined);
        }
      })
      .catch((err) => {
        setReadOnlyCard(false);
      });
  }, []);

  useEffect(() => {
    // DSSelect uses react-aria, which doesn't propagate data properties to the input elements,
    // so bypass it by setting them manually here
    const countrySelect = countrySelectRef.current?.querySelector('select');
    const stateSelect = stateSelectRef.current?.querySelector('select');
    if (countrySelect?.dataset) {
      countrySelect.dataset.recurly = 'country';
    }
    if (stateSelect?.dataset) {
      stateSelect.dataset.recurly = 'state';
    }
  }, []);

  let cleanup3DSecureListeners;
  useEffect(() => () => {
    cleanup3DSecureListeners?.();
  }, []);

  const saveBillingDataInRecurly = async (token, values, threeDsTokenId) => {
    await dispatch(updateBillingInfoAction({ token, values, threeDsTokenId })); // eslint-disable-line @typescript-eslint/await-thenable
    trackEvent(EVENTS.CHECKOUT_CARD_CHECK);
    history.push(`/${ROUTE_PARENT_SECTION}/${ROUTE_SECTION_CHECKOUT}`);
  };

  const scrollUpToTheErrorMessage = () => {
    const element = document.querySelector<HTMLElement>('#above-form-errors-alert');
    window.scrollTo(0, (element?.offsetTop || 0) + (element?.offsetHeight || 0));
  };

  const saveBillingData = async (token, values) => {
    try {
      await saveBillingDataInRecurly(token, values, null);
    } catch (error) {
      const { response } = error;
      const { remove3DListeners, promise } = handle3DSecure({
        onError: (innerError) => {
          setCardVerified(false);
          scrollUpToTheErrorMessage();
        },
        onVerified: async (receivedToken) => {
          await saveBillingDataInRecurly(token, values, receivedToken.threeDsToken.id);
          setCardVerified(recurlyJsCardOlbject?.vaid === true);
        },
        response,
      });

      cleanup3DSecureListeners = remove3DListeners;

      return promise;
    }
  };

  const onContinueButtonClicked = async (formValues, valid) => {
    if (valid) {
      const values = formatPaymentFormValues(formValues);
      dispatch(paymentInfoSavedAction(values));

      if (!readOnlyCard) {
        let token;
        try {
          const tokenResponse = await dispatch(fetchRecurlyTokenAction(formRef.current)); // eslint-disable-line @typescript-eslint/await-thenable
          token = tokenResponse.id;
          console.log('Received a token from Recurly.');
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          console.log(`Error occured while getting Recurly token: ${error.code} - ${error.message}`);
          setCardVerified(false);
          scrollUpToTheErrorMessage();
          return;
        }

        try {
          await saveBillingData(token, values);
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          console.log(`Error occured while saving billing data: ${error.code} - ${error.message}`);
          setCardVerified(false);
          scrollUpToTheErrorMessage();
        }
      }
    } else {
      scrollUpToTheErrorMessage();
    }
  };

  return (
    <Card className="w-full mr-2">
      <CardContent disableTopSpacing>
        <Form
          initialValues={initialValues}
          onSubmit={(values) => {
            history.push(`/${ROUTE_PARENT_SECTION}/${ROUTE_SECTION_CHECKOUT}`);
          }}
          render={({ form, handleSubmit }) => {
            const formState = form.getState();
            const { values } = formState;

            const invalidCard = !readOnlyCard && submitted && cardVerified === false;
            const invalidForm = submitted && (invalidCard || formState.hasValidationErrors);

            return (
              <form
                ref={formRef}
                onSubmit={handleSubmit}
              >
                {recurlyInstance && (
                  <RecurlyHelper
                    afterLoad={afterRecurlyLoad}
                    instance={recurlyInstance}
                  />
                )}

                {/* We need to scroll to the bottom of the element directly above
                FormErrorsInlineAlert. We cannot scroll to the top of FormErrorsInlineAlert
                as the element might not exist yet. */}
                <div id="above-form-errors-alert">
                  <Typography
                    className="mr-0.5 mb-3"
                    disableSpacing
                    variant="heading-3"
                  >
                    Company &amp; Contact Details
                  </Typography>
                </div>

                {invalidForm && <FormErrorsInlineAlert />}

                <DSInputText
                  autocompleteDisabled
                  className="mb-2"
                  data-recurly="company"
                  label="Company Name"
                  name="company"
                  placeholder="Company Name"
                  type="text"
                />
                <DSInputText
                  className="mb-2"
                  label="Billing Email Address"
                  name="email"
                  placeholder="Billing Email Address"
                />
                <DSInputText
                  className="mb-2"
                  data-recurly="address1"
                  label="Street Address"
                  name="address1"
                  placeholder="Street and number"
                />
                <div className="flex flex-row mb-2">
                  <div className="w-1/2 mr-1">
                    <div className="flex flex-row justify-between mb-0.5">
                      <SelectLabel label="Country" />
                      <CountryInfoIcon />
                    </div>
                    <DSSelect
                      data-qa="country-select"
                      items={countryOptions.map((country) => ({
                        label: country.text,
                        value: country.value,
                      }))}
                      name="country"
                      onChange={(countryCode) => {
                        // Adding onChange handler disables the default behavior of the select,
                        // so it's necessary to set the country here
                        form.change('country', countryCode);
                        form.change('state', null);
                      }}
                      placeholder="Select country"
                      selectionMode="single"
                      selectRef={countrySelectRef}
                    />
                  </div>
                  <div className="w-1/2 ml-1">
                    <DSSelect
                      data-qa="state-select"
                      disabled={!values.country}
                      items={getStateByCountryCode(values.country).map((state) => ({
                        label: state.text,
                        value: state.value,
                      }))}
                      label="State / Province"
                      name="state"
                      placeholder={values.country ? 'Select State / Province' : 'Select country first'}
                      selectionMode="single"
                      selectRef={stateSelectRef}
                    />
                  </div>
                </div>
                <div className="flex flex-row">
                  <div className="w-1/2 mr-1">
                    <DSInputText
                      data-recurly="city"
                      label="City"
                      name="city"
                    />
                  </div>
                  <div className="w-1/2 ml-1">
                    <DSInputText
                      data-recurly="postal_code"
                      label="ZIP"
                      name="postalCode"
                    />
                  </div>
                </div>
                <DSInputText
                  className="mb-2"
                  data-recurly="vat_number"
                  label="VAT ID"
                  name="vatNumber"
                  optional
                  optionalText="if applicable"
                />

                {!recurlyInstance && (
                  <Button
                    className="mr-3"
                    data-qa="display-payment-details-button"
                    onPress={() => {
                      if (values.country) {
                        dispatch(setRecurlyInstance(values.country));
                      }
                    }}
                    type="button"
                  >
                    Continue
                  </Button>
                )}

                <Typography
                  className="mr-0.5 mt-4 mb-3"
                  disableSpacing
                  variant="heading-3"
                >
                  Payment Details
                </Typography>

                {recurlyInstance && (
                  <>
                    {invalidCard ?
                      <CreditCardNotAcceptedInlineAlert /> :
                      <PaymentAuthenticationInlineAlert />}
                    <div className="mb-2">
                      <CreditCardDetails
                        handleReplaceCard={handleReplaceCard}
                        readOnlyCard={readOnlyCard}
                      />
                    </div>
                    <div className="flex flex-row mb-2">
                      <div className="w-1/2 mr-1">
                        <DSInputText
                          data-recurly="first_name"
                          label="First Name"
                          name="firstName"
                        />
                      </div>
                      <div className="w-1/2 ml-1">
                        <DSInputText
                          data-recurly="last_name"
                          label="Last Name"
                          name="lastName"
                        />
                      </div>
                    </div>

                    <div className="flex flex-row mb-3">
                      <IconLockFill className="text-gray-500 fill-current w-2 mr-1 self-center" />
                      <Typography
                        className="text-gray-600 mr-0.5"
                        disableSpacing
                        variant="body-large"
                      >
                        Guaranteed
                      </Typography>
                      <Typography
                        className="text-gray-600 font-semibold mr-0.5"
                        disableSpacing
                        variant="body-large"
                      >
                        safe and secure checkout
                      </Typography>
                      <Typography
                        className="text-gray-600"
                        disableSpacing
                        variant="body-large"
                      >
                        powered by Stripe and Recurly
                      </Typography>
                    </div>

                    <div className="flex flex-row">
                      <ContinueButton
                        onContinueClicked={() => onContinueButtonClicked(
                          values,
                          !formState.hasValidationErrors,
                        )}
                        setSubmitted={setSubmitted}
                      />
                      <Typography
                        className="text-high-emphasis self-center"
                        disableSpacing
                        variant="body-large"
                      >
                        You can view this order and claim a coupon code in the next step.
                      </Typography>
                    </div>
                  </>
                )}
                {!recurlyInstance && (
                  <CreditCardNotShownUntilCountryKnown />
                )}
              </form>
            );
          }}
          validate={(values) => {
            let validationResult = validate(
              values,
              constraints,
              { fullMessages: false },
            ) as ValidationError;

            // Form is invalid if provided card details are invalid.
            if (!recurlyJsCardObject?.valid && !readOnlyCard) {
              // Card component does not support validation (inline alert is used instead)
              // but we need to return an error here anyway to establish that the form is invalid.
              const validationError = { validationResult: 'Please enter valid card details' };
              if (!validationResult) {
                validationResult = { cardDetails: validationError };
              } else {
                validationResult.cardDetails = validationError;
              }
              setCardVerified(false);
            }
            return validationResult;
          }}
        />
      </CardContent>
    </Card>
  );
};
